import { Component, OnInit, ElementRef, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiCatalogService, ApiFileInfo } from '../shared/services/api-catalog/api-catalog.service';
import { SwaggerUIService } from '../shared/services/swagger/swagger-ui.service';
import { CommonModule } from '@angular/common';
import { VersionSelectorComponent } from '../shared/components/version-selector/version-selector.component';
import { ApiVersion } from '../shared/models/api-catalog.models';
import { combineLatest, Subject, from } from 'rxjs';
import { tap, delay, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-api-viewer',
  standalone: true,
  imports: [CommonModule, VersionSelectorComponent],
  templateUrl: './api-viewer.component.html',
  styleUrls: ['./api-viewer.component.scss']
})
export class ApiViewerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('swaggerContainer', { static: false }) swaggerContainer!: ElementRef;
  
  apiInfo: ApiFileInfo | null = null;
  loading = false;
  error: string | null = null;
  activeTab = 'swagger';
  swaggerUIInstance: any = null;
  
  // Version management
  availableVersions: ApiVersion[] = [];
  currentVersionId: string = '';
  currentApiTitle: string = '';
  
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiCatalogService: ApiCatalogService,
    private swaggerUIService: SwaggerUIService,
  ) { }

  ngOnInit() {
    this.route.params.pipe(
      switchMap(params => {
        const fileIdentifier = params['fileIdentifier'];
        if (fileIdentifier) {
          this.loading = true;
          this.error = null;
          this.currentVersionId = fileIdentifier;
          
          // Load API specification and catalog list in parallel
          return combineLatest([
            this.apiCatalogService.getApiSpecById(fileIdentifier),
            this.apiCatalogService.getApiCatalogList()
          ]).pipe(
            tap(([content, catalogResponse]) => {
              this.apiInfo = this.apiCatalogService.extractFileInfo(content);
              this.apiInfo.apiId = fileIdentifier;
              this.currentApiTitle = this.apiInfo.title;

              const allVersionsOfThisApi = catalogResponse.data
                .filter((item: any) => item.title === this.currentApiTitle);

              this.availableVersions = this.apiCatalogService
                .sortApiVersions(allVersionsOfThisApi);
            }),
            delay(100),
            switchMap(() => from(this.initializeSwaggerUI()))
          );
        }
        return [];
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => { 
        this.loading = false; 
      },
      error: (err) => {
        this.error = err?.message || 'Failed to load API specification or initialize Swagger UI';
        this.loading = false;
      }
    });
  }

  ngAfterViewInit() {
  }

  ngOnDestroy() {
    // Cancel all active subscriptions
    this.destroy$.next();
    this.destroy$.complete();
    
    // Clean up Swagger UI instance
    if (this.swaggerUIInstance) {
      this.swaggerUIInstance = null;
    }
  }
  
  /**
   * Handle version selection from the version selector component
   * @param version - Selected API version
   */
  onVersionChange(version: ApiVersion): void {
    if (version.apiId !== this.currentVersionId) {
      this.router.navigate(['/api-viewer', version.apiId], {
        replaceUrl: true
      });
    }
  }


  async initializeSwaggerUI() {
    if (!this.apiInfo || !this.swaggerContainer) {
      console.error('Missing apiInfo or swaggerContainer');
      this.error = 'Missing required data for Swagger UI initialization';
      return;
    }

    if (!this.apiInfo.parsedContent) {
      console.error('No parsed content available for Swagger UI');
      this.error = 'Invalid API specification - no parsed content available';
      return;
    }

    try {
      console.log('Initializing Swagger UI with spec:', this.apiInfo.parsedContent);
      
      // Clear existing Swagger UI
      if (this.swaggerUIInstance) {
        this.swaggerUIService.clearSwaggerUI(this.swaggerContainer.nativeElement);
      }

      // Initialize Swagger UI using the main service with authentication
      this.swaggerUIInstance = await this.swaggerUIService.initializeSwaggerUI(
        this.swaggerContainer.nativeElement,
        this.apiInfo.parsedContent
      );

      console.log('Swagger UI initialized successfully');

    } catch (error) {
      console.error('Error initializing Swagger UI:', error);
      this.error = `Failed to initialize Swagger UI: ${error instanceof Error ? error.message : 'Unknown error'}`;
    }
  }

  formatJson(content: any): string {
    return JSON.stringify(content, null, 2);
  }

  retrySwaggerUI() {
    this.error = null;
    this.initializeSwaggerUI();
  }

  get SwaggerUIBundle() {
    return (window as any).SwaggerUIBundle;
  }

  goBack() {
    this.router.navigate(['/apis']);
  }
  
}
